import React, { useEffect, useState, useContext } from 'react';
import parse, { DOMNode } from 'html-react-parser';
import { Box, Button, colors, Stack, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { usePolicyEditorContext } from '../context';
import { DraftManualDetailContext } from '../../context';
import Modal from 'components/_layouts/Modal';
import { PolicyComparison } from './policy_comparison';
import policyComparer from './comparer';
import { getContentForDisplay } from '../../../helpers/contentDisplayHelper';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';

type PolicyCompareProps = {
  id: string;
};

const PolicyCompare: React.FC<PolicyCompareProps> = ({ id }) => {
  const { committedPolicyQuery, policyNumber, policyData, revertChanges } = usePolicyEditorContext();
  const { isComparePolicyOpen, setIsComparePolicyOpen, textEntities } = useContext(DraftManualDetailContext);
  const [comparison, setComparison] = useState<PolicyComparison | null>(null);

  const handleAcceptChanges = () => {
    localStorage.removeItem(`policy-lock_${id}`);
    setIsComparePolicyOpen(false);
  };

  const handleRevertChanges = async () => {
    await revertChanges();
    localStorage.removeItem(`policy-lock_${id}`);
    setIsComparePolicyOpen(false);
  };

  useEffect(() => {
    if (committedPolicyQuery.data?.sections && policyData?.sections && isComparePolicyOpen) {
      setComparison(policyComparer.compare(committedPolicyQuery.data, policyData, policyNumber));
    }
  }, [committedPolicyQuery.data, isComparePolicyOpen, policyData, policyNumber]);

  return (
    <Modal
      title={t('draftManuals.policyCompare.title')}
      size='lg'
      open={isComparePolicyOpen}
      onClose={() => handleAcceptChanges()}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          columnGap: 4,
        }}
      >
        <Typography variant='subtitle1'>{t('draftManuals.policyCompare.versions.committed')}</Typography>
        <Typography variant='subtitle1'>{t('draftManuals.policyCompare.versions.staged')}</Typography>
        <Box
          sx={{
            height: '50vh',
            overflow: 'auto',
            border: `solid 1px ${colors.grey[200]}`,
            p: 4,
            '& del': {
              textDecoration: 'line-through',
              color: '#b30000',
            },
            '& ins': {
              textDecoration: 'underline double',
              color: '#406619',
              display: 'none',
            },
            '& p>span>ins': {
              display: 'block',
            },
          }}
        >
          {comparison && (
            <Stack spacing={3}>
              <Typography
                component='div'
                variant='h5'
              >
                {parse(comparison.title)}
              </Typography>
              <Stack spacing={3}>
                {comparison.sections.map((section) => (
                  <Box key={section.id}>
                    <Stack spacing={1}>
                      <Typography
                        className={`title_${section.id}`}
                        component='div'
                        variant='h6'
                      >
                        {parse(section.title)}
                      </Typography>
                      <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                        {parse(getContentForDisplay(section.content ?? '', textEntities), {
                          replace(node: DOMNode, ind: number) {
                            return PolicyContentHelper.handleContent(node, ind);
                          },
                        })}
                      </Box>
                    </Stack>
                    <Stack
                      spacing={3}
                      sx={{ ml: 5, mt: 3 }}
                    >
                      {section.subSections.map((subSection) => (
                        <Box key={subSection.id}>
                          <Stack spacing={1}>
                            <Typography
                              className={`title_${subSection.id}`}
                              component='div'
                              variant='h6'
                            >
                              {parse(subSection.title)}
                            </Typography>
                            <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                              {parse(getContentForDisplay(subSection.content ?? '', textEntities), {
                                replace(node: DOMNode, ind: number) {
                                  return PolicyContentHelper.handleContent(node, ind);
                                },
                              })}
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            height: '50vh',
            overflow: 'auto',
            border: `solid 1px ${colors.grey[200]}`,
            p: 4,
            '& del': {
              textDecoration: 'line-through',
              color: '#b30000',
              display: 'none',
            },
            '& p>span>del': {
              display: 'block',
            },
            '& ins': {
              textDecoration: 'underline double',
              color: '#406619',
            },
          }}
        >
          {comparison && (
            <Stack spacing={3}>
              <Typography
                component='div'
                variant='h5'
              >
                {parse(comparison.title)}
              </Typography>
              <Stack spacing={3}>
                {comparison.sections.map((section) => (
                  <Box key={section.id}>
                    <Stack spacing={1}>
                      <Typography
                        className={`title_${section.id}`}
                        component='div'
                        variant='h6'
                      >
                        {parse(section.title)}
                      </Typography>
                      <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                        {parse(getContentForDisplay(section.content ?? '', textEntities), {
                          replace(node: DOMNode, ind: number) {
                            return PolicyContentHelper.handleContent(node, ind);
                          },
                        })}
                      </Box>
                    </Stack>
                    <Stack
                      spacing={3}
                      sx={{ ml: 5, mt: 3 }}
                    >
                      {section.subSections.map((subSection) => (
                        <Box key={subSection.id}>
                          <Stack spacing={1}>
                            <Typography
                              className={`title_${subSection.id}`}
                              component='div'
                              variant='h6'
                            >
                              {parse(subSection.title)}
                            </Typography>
                            <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                              {parse(getContentForDisplay(subSection.content ?? '', textEntities), {
                                replace(node: DOMNode, ind: number) {
                                  return PolicyContentHelper.handleContent(node, ind);
                                },
                              })}
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          <Button
            color='error'
            onClick={handleRevertChanges}
          >
            {t('draftManuals.policyCompare.actions.reject')}
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          <Button
            variant='contained'
            color='success'
            onClick={handleAcceptChanges}
          >
            {t('draftManuals.policyCompare.actions.accept')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PolicyCompare;
